import { useState } from "react";
import { Mail, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { masterApi, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";

const cls = "h-12 bg-slate-50 border-slate-200";

export const ForgotOtpForm = ({ initialEmail = "", onDone, onBack, emailPlaceholder = "Store email or mobile number", accent = "bg-[#1E3A8A] hover:bg-[#152a63]" }) => {
  const [channel, setChannel] = useState("email");
  const [ident, setIdent] = useState(initialEmail);
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState({ nw: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!ident.trim()) return toast.error(channel === "email" ? "Enter your store email" : "Enter your store mobile number");
    setBusy(true);
    try { const { data } = await masterApi.post("/auth/forgot-otp", { identifier: ident.trim(), channel }); setSent(data); toast.success(data.sent_to ? `Code sent to ${data.sent_to}` : data.message); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const verify = async () => {
    if (pw.nw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw.nw !== pw.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try { const { data } = await masterApi.post("/auth/forgot-otp/verify", { identifier: ident.trim(), code, new_password: pw.nw }); toast.success("Password updated — please log in"); onDone(data.email); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="forgot-otp-form">
      <p className="font-extrabold text-slate-900 text-center">Reset your password</p>
      <div className="grid grid-cols-2 gap-2">
        {[["email", "Email code", Mail], ["sms", "SMS (soon)", Smartphone]].map(([k, l, Icon]) => (
          <button key={k} data-testid={`forgot-channel-${k}`} disabled={!!sent} onClick={() => { setChannel(k); setIdent(""); }}
            className={`h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 ${channel === k ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600"}`}><Icon className="w-4 h-4" /> {l}</button>
        ))}
      </div>
      {channel === "sms" && <p data-testid="forgot-sms-soon" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">SMS reset codes go live once SMS is connected. For now, choose Email code — you can type your store email or your mobile number and we'll email the code.</p>}
      <Input data-testid="forgot-identifier" disabled={!!sent} value={ident} onChange={(e) => setIdent(e.target.value)} inputMode={channel === "sms" ? "tel" : "email"}
        placeholder={channel === "email" ? emailPlaceholder : "Store mobile number"} className={cls} />
      {!sent ? (
        <Button data-testid="forgot-send" disabled={busy} onClick={send} className={`w-full h-12 ${accent} text-white font-bold`}>{busy ? "Sending…" : "Send 6-digit code"}</Button>
      ) : (<>
        <Input data-testid="forgot-code" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`${cls} tracking-[0.4em] text-center font-mono text-lg`} />
        <PasswordInput data-testid="forgot-new-password" placeholder="New password" value={pw.nw} onChange={(e) => setPw({ ...pw, nw: e.target.value })} className={cls} />
        <PasswordInput data-testid="forgot-confirm-password" placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={cls} />
        <Button data-testid="forgot-verify" disabled={busy || code.length !== 6} onClick={verify} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">Set New Password</Button>
        <button data-testid="forgot-resend" onClick={() => { setSent(null); setCode(""); }} className="w-full text-center text-blue-600 text-sm font-medium">Didn't get it? Send again</button>
      </>)}
      <button data-testid="forgot-back" onClick={onBack} className="w-full text-center text-slate-400 hover:text-slate-600 text-sm">← Back to login</button>
    </div>
  );
};
